import React, { useState } from 'react';
import { Flag, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'nudity_sexual',      label: 'Nudity / Sexual content' },
  { id: 'violence_graphic',   label: 'Violence / Graphic content' },
  { id: 'hate_harassment',    label: 'Hate speech / Harassment' },
  { id: 'dangerous_self_harm',label: 'Dangerous acts / Self-harm' },
  { id: 'illegal_activities', label: 'Illegal activities (drugs, weapons)' },
  { id: 'misinformation',     label: 'Misinformation' },
  { id: 'spam_scam',          label: 'Spam / Scam' },
  { id: 'copyright',          label: 'Copyright / Trademark' },
  { id: 'minor_safety',       label: 'Minor safety' },
  { id: 'other',              label: 'Other' },
];

/**
 * ReportButton — small flag icon that opens a modal for policy reports.
 *
 * Props:
 *   videoType  "intro" | "library" | "youtube" | "lesson"
 *   videoId    id of the underlying asset (user_id for "intro")
 *   variant    "icon" | "pill" — visual style; defaults to "icon"
 *   className  extra classes for outer button
 */
export default function ReportButton({ videoType, videoId, variant = 'icon', className = '' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('spam_scam');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) {
      toast.error('Please sign in to report content.');
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/reports/video`,
        {
          video_type: videoType,
          video_id: videoId,
          category,
          description: desc.trim(),
          context_url: typeof window !== 'undefined' ? window.location.href : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.duplicate ? 'Already reported — thanks.' : 'Report submitted. Thank you.');
      setOpen(false);
      setDesc('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit report.');
    } finally {
      setBusy(false);
    }
  };

  const trigger = variant === 'pill' ? (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 hover:bg-red-500/15 text-gray-400 hover:text-red-300 text-[11px] font-medium transition-colors ${className}`}
      data-testid={`report-btn-${videoType}-${videoId}`}
      title="Report this content"
    >
      <Flag className="w-3 h-3" /> Report
    </button>
  ) : (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
      className={`w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-black/80 transition-colors ${className}`}
      data-testid={`report-btn-${videoType}-${videoId}`}
      title="Report this content"
      aria-label="Report this content"
    >
      <Flag className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="report-modal"
        >
          <div className="w-full max-w-md rounded-2xl bg-brand-surface border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Flag className="w-4 h-4 text-red-400" /> Report content
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-white"
                data-testid="report-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">Why are you reporting this?</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-brand-gold/60 outline-none"
                  data-testid="report-category-select"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">Details (optional)</label>
                <textarea
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Add any context that will help our moderators…"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-brand-gold/60 outline-none resize-none"
                  data-testid="report-description-input"
                />
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Reports are reviewed against WeMasterminds Community Guidelines
                (aligned with TikTok policy). Upheld reports result in the
                content being hidden immediately.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2 bg-black/20">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white"
                data-testid="report-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50"
                data-testid="report-submit-btn"
              >
                {busy ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
